import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import {
  AddDevicesToUserParams,
  AssignDevicesToInviteeParams,
  CreateSubcontractParams,
  EditInvitationParams,
  EditSubcontractParams,
  SetUserDevicesParams,
} from "@/api/MTCallTypesAndParams";
import { useGlobalConfigsStore } from "../globalConfigs/globalConfigs";
import { MainContract, Service, Subcontract } from "@/components/views/MultiTenancy/multiTenancyTypes";
import { CreateDashboardWithWidgetsParams } from "@/types";
import { mockPredefinedTemplates } from "@/components/views/MultiTenancy/customerMockData";

const CONNECTOR_NOT_INITIALIZED_ERROR = "Connector not initialized";

export const useMultiTenancyStore = defineStore("multiTenancy", () => {
  const globalStore = useGlobalConfigsStore();

  const mainContract = ref<MainContract>();
  const customers = ref<Subcontract[]>([]);
  const userRolesForInvites = ref<string[]>([]);
  const isPredefinedTemplatesPanelOpen = ref(false);
  const selectedCustomerId = ref<string>("all");

  const filteredCustomers = computed(() => {
    if (selectedCustomerId.value === "all") {
      return customers.value;
    }

    return customers.value.filter((customer) => customer.id === selectedCustomerId.value);
  });

  const selectedCustomer = computed(() => {
    return customers.value.find((customer) => customer.id === selectedCustomerId.value);
  });

  async function loadMainContract() {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }

    mainContract.value = await globalStore.connector.multiTenancy.getMainContract();
    customers.value = mainContract.value.subcontracts;
  }

  function setSelectedCustomerId(customerId: string) {
    selectedCustomerId.value = customerId;
  }

  async function createCustomer(data: CreateSubcontractParams): Promise<string> {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    const { subContractId } = await globalStore.connector.multiTenancy.createSubcontract({
      customer: data.customer,
      contact: data.contact,
      timezone: data.timezone,
      canSetRemoteControlLimit: data.canSetRemoteControlLimit,
      sendReminderEmail: data.sendReminderEmail,
      enabledServices: data.enabledServices,
      predeterminedDurationStartDate: data.predeterminedDurationStartDate,
      predeterminedDurationEndDate: data.predeterminedDurationEndDate,
    });

    customers.value.push({
      id: subContractId,
      details: {
        customer: data.customer,
        contact: data.contact,
        timezone: data.timezone,
        canSetRemoteControlLimit: data.canSetRemoteControlLimit,
        sendReminderEmail: data.sendReminderEmail,
        predeterminedDurationStartDate: data.predeterminedDurationStartDate ?? null,
        predeterminedDurationEndDate: data.predeterminedDurationEndDate ?? null,
        services: mapServiceByIdentifier(data.enabledServices),
        isActive: true,
        isLimited: Boolean(data.predeterminedDurationStartDate || data.predeterminedDurationEndDate),
        logo: "",
        createdAt: Date.now(),
      },
      devices: [],
      users: [],
    });

    return subContractId;
  }

  async function editCustomer(id: string, data: EditSubcontractParams): Promise<string> {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    await globalStore.connector.multiTenancy.editSubcontract({
      id,
      customer: data.customer,
      contact: data.contact,
      timezone: data.timezone,
      isActive: data.isActive,
      canSetRemoteControlLimit: data.canSetRemoteControlLimit,
      sendReminderEmail: data.sendReminderEmail,
    });

    const index = getCustomerIndexById(id);
    customers.value[index] = {
      ...customers.value[index],
      details: {
        ...customers.value[index].details,
        customer: data.customer,
        contact: data.contact,
        timezone: data.timezone,
        isActive: data.isActive,
        canSetRemoteControlLimit: data.canSetRemoteControlLimit,
        sendReminderEmail: data.sendReminderEmail,
      },
    };

    return id;
  }

  async function getUserRolesForInvites() {
    userRolesForInvites.value = (await globalStore.connector?.multiTenancy.getRolesForInvitees()) ?? [];
  }

  async function inviteUser({ customerId, userData }: { customerId: string; userData: { email: string; role: string } }) {
    const customer = customers.value.find((c) => c.id === customerId);
    if (!customer) {
      throw new Error("Customer not found");
    }
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    return await globalStore.connector?.multiTenancy.inviteNewUser({
      contract: customer.id,
      email: userData.email,
      role: userData.role,
      deviceIds: [],
    });
  }

  async function getInvitees(customerId: string) {
    return await globalStore.connector?.multiTenancy.getUserInvitations(customerId);
  }

  async function assignDevicesToInvitee(params: AssignDevicesToInviteeParams) {
    return await globalStore.connector?.multiTenancy.assignDevicesToInvitee(params);
  }

  async function addDevicesToUser(params: AddDevicesToUserParams) {
    return await globalStore.connector?.multiTenancy.addDevicesToUser(params);
  }

  async function setUserDevices(params: SetUserDevicesParams) {
    return await globalStore.connector?.multiTenancy.setUserDevices(params);
  }

  async function setUserRole(params: { userid: number; role: string }) {
    if (!globalStore.connector?.userManagement) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    const result = await globalStore.connector.userManagement.updateRolesOrThrow([params]);
    if (typeof result === "string" && result.toLocaleLowerCase().startsWith("error:")) {
      throw new Error(result.substring(6).trim());
    }
    return result;
  }

  async function deleteInvitation(token: string) {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    await globalStore.connector.multiTenancy.deleteInvitation(token);
  }

  async function editInvitation(params: EditInvitationParams) {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    await globalStore.connector.multiTenancy.editInvitation(params);
  }

  async function resendInvitation(token: string) {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    await globalStore.connector.multiTenancy.resendInvitation(token);
  }

  async function setDeviceLocation(
    deviceId: number,
    locationData: {
      latitude?: number;
      longitude?: number;
      name: string;
    }
  ) {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    await globalStore.connector.multiTenancy.setDeviceLocation({
      device: deviceId,
      latitude: locationData.latitude ?? null,
      longitude: locationData.longitude ?? null,
      name: locationData.name,
    });
    const deviceIndex = selectedCustomer.value?.devices.findIndex((d) => d.device.id === deviceId) ?? -1;
    if (!selectedCustomer.value?.devices.length || deviceIndex === -1) {
      throw new Error("Device not found");
    }
    if (locationData.latitude && locationData.longitude) {
      selectedCustomer.value.devices[deviceIndex].device.location = {
        latitude: locationData.latitude ?? null,
        longitude: locationData.longitude ?? null,
        name: locationData.name,
      };
      return;
    }
    selectedCustomer.value.devices[deviceIndex].device.location = null;
  }

  async function setCustomerLogo(subContractId: string, logo: string) {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }
    if (logo === "") {
      await globalStore.connector.multiTenancy.deleteSubcontractCustomerLogo(subContractId);
      return;
    } else {
      await globalStore.connector.multiTenancy.setSubcontractCustomerLogo({ logo, subContractId });
    }

    const index = getCustomerIndexById(subContractId);
    customers.value[index].details.logo = logo;
  }

  function getCustomerIndexById(id: string) {
    const index = customers.value.findIndex((c) => c.id === id);
    if (index === -1) {
      throw new Error("Customer not found");
    }
    return index;
  }

  function mapServiceByIdentifier(identifier: string[]): Service[] {
    if (!mainContract.value?.services.length) return [];

    return identifier.map((i) => mainContract.value?.services.find((s) => s.identifier === i)).filter((s) => !!s);
  }

    async function getMTPredefinedTemplates() {
    //return await globalStore.connector?.multiTenancy.getMTPredefinedTemplates() ?? [];
    return mockPredefinedTemplates;
  }

//TODO: Implement these functions in the backend and remove the mock implementation.

// async function createDashboard(params: CreateDashboardParams) {
// 	if (!globalStore.connector?.multiTenancy) {
// 		throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
// 	}

// 	return await globalStore.connector.uiSettings.createDashboard(params.title);
// }

// async function createCard(params: CreateCardParams) {
// 	if (!globalStore.connector?.multiTenancy) {
// 		throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
// 	}

// 	return await globalStore.connector.uiSettings.createCard({
// 		dashboardID: params.dashboardId,
// 		title: params.title,
// 		size: params.size,
// 	});
// }

// async function createTemplateBasedWidgetsMT(params: CreateValueWithRangeWidgetParams) {
// 	if (!globalStore.connector?.multiTenancy) {
// 		throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
// 	}

// 	return await globalStore.connector.uiSettings.createWidget({
// 		type: params.type,
// 		cardID: params.cardId,
// 		data: JSON.stringify(params.data),
// 	});
// }

  async function createMTTemplateBasedWidgets(params: CreateDashboardWithWidgetsParams) {
    if (!globalStore.connector?.multiTenancy) {
      throw new Error(CONNECTOR_NOT_INITIALIZED_ERROR);
    }

    return await globalStore.connector.multiTenancy.createMTDashboardWithWidgets(params);
  }

  watch(
    () => mainContract.value,
    (newContract) => {
      customers.value = newContract?.subcontracts ?? [];
    }
  );

  return {
    mainContract,
    customers,
    userRolesForInvites,
    isPredefinedTemplatesPanelOpen,
    loadMainContract,
    createCustomer,
    editCustomer,
    setCustomerLogo,
    getUserRolesForInvites,
    inviteUser,
    getInvitees,
    assignDevicesToInvitee,
    addDevicesToUser,
    mapServiceByIdentifier,
    selectedCustomerId,
    filteredCustomers,
    selectedCustomer,
    setSelectedCustomerId,
    setUserDevices,
    setUserRole,
    deleteInvitation,
    editInvitation,
    resendInvitation,
    setDeviceLocation,
    getMTPredefinedTemplates,
    createMTTemplateBasedWidgets,
  };
});
